'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DndContext, closestCenter, DragOverlay, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Users, Plus, Search, List, Columns, X,
  Edit2, Trash2, ArrowRightCircle, Phone, Mail, Tag,
  Download, Upload, Crosshair, Activity, Building2, User, Hash, Globe, ChevronRight, ChevronLeft,
  MoreVertical, Filter, ArrowUpRight
} from 'lucide-react';
import {
  fetchLeads, createLead, updateLead, deleteLead, convertLead, fetchPipelines
} from '../../redux/crmSlice';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import ImportModal from '../../components/crm/ImportModal';
import ConvertToDealModal from '../../components/crm/ConvertToDealModal';
import { SUPPORTED_CURRENCIES } from '../../constants/currencies';
import { getCurrencySymbol, convertCurrency } from '../../utils/currency';
import { useCurrencyRates, useCompanyCurrency } from '../../hooks/useCompanySettings';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkActionBar from '../../components/crm/BulkActionBar';
import SavedViewsBar from '../../components/crm/SavedViewsBar';
import FilterBuilder from '../../components/crm/FilterBuilder';
import InlineEditSelect from '../../components/crm/InlineEditSelect';
import RowActionsMenu from '../../components/crm/RowActionsMenu';
import { downloadCsv } from '../../utils/exportCsv';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
// ── Constants ─────────────────────────────────────────────
const PAGE_SIZE = 20;
// Kanban needs the whole active pipeline visible to drag between columns —
// a paginated fetch would only ever show 20 leads total across every column.
const KANBAN_FETCH_LIMIT = 1000;

const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'lost'];
const SOURCES = ['whatsapp', 'web_form', 'manual', 'bulk_import', 'instagram', 'api', 'referral', 'conversation_flow'];
const LIFECYCLE_STAGES = ['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer', 'evangelist', 'other'];

const LEAD_FILTER_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phoneNumber', label: 'Phone' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Source' },
  { value: 'stage', label: 'Stage' },
  { value: 'score', label: 'Lead Score' },
  { value: 'expectedValue', label: 'Expected Value' },
  { value: 'lifecycleStage', label: 'Lifecycle Stage' },
];

const EMPTY_FORM = {
  name: '', phoneNumber: '', email: '', source: 'whatsapp',
  status: 'new', stage: '', expectedValue: '', currency: 'INR', notes: '', tags: '', pipelineId: '', departmentId: '',
};

// ── Components ─────────────────────────────────────────────

function ScoreBar({ score = 0 }) {
  return (
    <div className="flex items-center gap-2 w-24">
      <div className="flex-1 h-1.5 bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
        <div 
          className="h-full bg-[#0176D3] transition-all duration-1000" 
          style={{ width: `${Math.min(100, score)}%` }} 
        />
      </div>
      <span className="text-[10px] font-bold text-[#706E6B] tabular-nums">{score}</span>
    </div>
  );
}

function LeadBadge({ status }) {
  const badgeClass = 
    status === 'converted' ? 'sf-badge-success' :
    status === 'lost' ? 'sf-badge-error' :
    status === 'qualified' ? 'sf-badge-warning' :
    status === 'contacted' ? 'sf-badge-neutral' : 'sf-badge-neutral';
  
  return (
    <span className={cn("sf-badge", badgeClass)}>
      {status}
    </span>
  );
}

function SortableLeadCard({ lead, onEdit, onConvert, onDelete }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lead._id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className={cn(
          "bg-white border border-[#E5E5E5] rounded-lg p-4 mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:border-[#0176D3] transition-all group cursor-grab active:cursor-grabbing",
          isDragging && "opacity-40 scale-95 ring-2 ring-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)]"
        )}
      >
        <div className="flex items-start justify-between">
          <div className="space-y-0.5 flex-1 pr-2 min-w-0">
            <h4 onClick={() => navigate(`/dashboard/crm/leads/${lead._id}`)} className="text-[13px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors truncate cursor-pointer hover:underline">{lead.name}</h4>
            <div className="flex items-center gap-1.5 text-[11px] text-[#706E6B]">
              <Phone size={10} />
              <span className="tabular-nums">{lead.phoneNumber || 'No phone'}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-4">
          <span className="sf-badge sf-badge-neutral lowercase">{lead.source || 'manual'}</span>
          <ScoreBar score={lead.score || 0} />
        </div>

        <div className="flex items-center justify-end gap-1.5 mt-4 pt-3 border-t border-[#D8DDE6] opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(lead)} title="Edit" aria-label="Edit lead" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors"><Edit2 size={12} /></button>
          <button onClick={() => onConvert(lead)} title="Convert to Deal" aria-label="Convert lead to deal" className="p-1.5 text-[#2E844A] hover:bg-[#EBF3ED] rounded transition-colors"><ArrowUpRight size={12} /></button>
          <button onClick={() => onDelete(lead)} title="Delete" aria-label="Delete lead" className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors"><Trash2 size={12} /></button>
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({ status, leads, onEdit, onConvert, onDelete }) {
  const { setNodeRef, isOver } = useSortable({ id: status });

  return (
    <div ref={setNodeRef} className={cn(
      "flex flex-col w-72 shrink-0 h-full rounded bg-[#F4F6F9] border border-[#D8DDE6] transition-colors",
      isOver && "border-[#0176D3] bg-[#0176D3]/[0.02]"
    )}>
      <div className="p-4 border-b border-[#D8DDE6] flex items-center justify-between bg-white rounded-t">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-2 h-2 rounded-full",
            status === 'converted' ? 'bg-[#2E844A]' :
            status === 'lost' ? 'bg-[#BA0517]' :
            status === 'qualified' ? 'bg-[#FF9900]' :
            status === 'contacted' ? 'bg-[#0176D3]' : 'bg-[#706E6B]'
          )} />
          <span className="text-[11px] font-bold text-[#080707] uppercase tracking-wider">{status}</span>
        </div>
        <span className="text-[10px] font-bold text-[#706E6B] bg-[#F3F2F2] px-2 py-0.5 rounded-full">{leads.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-0">
        <SortableContext items={leads.map(l => l._id)} strategy={verticalListSortingStrategy}>
          {leads.map(lead => (
            <SortableLeadCard key={lead._id} lead={lead} onEdit={onEdit} onConvert={onConvert} onDelete={onDelete} />
          ))}
        </SortableContext>
        {leads.length === 0 && (
          <div className="py-12 flex flex-col items-center justify-center border-2 border-dashed border-[#D8DDE6] rounded text-[10px] font-bold text-[#706E6B] uppercase tracking-widest opacity-40">
            Drop Here
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeadsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { leads, leadsMeta, pipelines, loading, error } = useSelector(state => state.crm);
  const currentUserEmail = useSelector(state => state.auth?.user?.email);

  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [myLeadsOnly, setMyLeadsOnly] = useState(false);
  const [advancedConditions, setAdvancedConditions] = useState([]);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [convertingLead, setConvertingLead] = useState(null);
  const bulk = useBulkSelection();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const ratesCtx = useCurrencyRates();
  const companyCurrency = useCompanyCurrency();

  useEffect(() => {
    dispatch(fetchPipelines());
    api.get('/departments').then(res => setDepartments(res.data?.departments || [])).catch(() => {});
  }, [dispatch]);

  const handleCurrencyChange = (newCurrency) => {
    setFormData(p => {
      if (!p.expectedValue || p.currency === newCurrency) return { ...p, currency: newCurrency };
      // Re-price the number itself, not just relabel it — $100 switched to
      // INR should become the INR equivalent, not "100" tagged as rupees.
      const converted = convertCurrency(p.expectedValue, p.currency, newCurrency, ratesCtx);
      return { ...p, currency: newCurrency, expectedValue: Math.round(converted * 100) / 100 };
    });
  };

  // Reset to page 1 whenever the search/filters change so results aren't scoped to a stale page
  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterSource, filterDepartment, myLeadsOnly, advancedConditions]);

  const advancedFiltersParam = useMemo(() => {
    const valid = advancedConditions.filter(c => c.field && c.value !== '');
    return valid.length ? JSON.stringify(valid) : undefined;
  }, [advancedConditions]);

  useEffect(() => {
    if (view === 'kanban') {
      dispatch(fetchLeads({ limit: KANBAN_FETCH_LIMIT }));
    } else {
      dispatch(fetchLeads({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        source: filterSource !== 'all' ? filterSource : undefined,
        departmentId: filterDepartment !== 'all' ? filterDepartment : undefined,
        assignedTo: myLeadsOnly ? currentUserEmail : undefined,
        advancedFilters: advancedFiltersParam,
      }));
    }
  }, [dispatch, view, page, search, filterStatus, filterSource, filterDepartment, myLeadsOnly, currentUserEmail, advancedFiltersParam]);

  // Changing pages swaps the table body but leaves the page scrolled to
  // wherever the user was — reset to the top so the new page starts in view.
  // The scrollable element is DashboardLayout's <main>, not window, since
  // the sidebar/header are fixed and only <main> scrolls.
  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [page]);

  const refetchLeads = useCallback(() => {
    if (view === 'kanban') {
      dispatch(fetchLeads({ limit: KANBAN_FETCH_LIMIT }));
    } else {
      dispatch(fetchLeads({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        source: filterSource !== 'all' ? filterSource : undefined,
        departmentId: filterDepartment !== 'all' ? filterDepartment : undefined,
        assignedTo: myLeadsOnly ? currentUserEmail : undefined,
        advancedFilters: advancedFiltersParam,
      }));
    }
  }, [dispatch, view, page, search, filterStatus, filterSource, filterDepartment, myLeadsOnly, currentUserEmail, advancedFiltersParam]);

  const handleApplyView = (filters) => {
    setSearch(filters.search || '');
    setFilterStatus(filters.status || 'all');
    setFilterSource(filters.source || 'all');
    setAdvancedConditions(filters.advancedConditions || []);
  };

  const totalLeads = leadsMeta?.total ?? leads.length;
  const totalPages = leadsMeta?.pages || 1;

  const filtered = useMemo(() => {
    return leads.filter(l => {
      const matchSearch = !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.phoneNumber?.includes(search);
      const matchStatus = filterStatus === 'all' || l.status === filterStatus;
      const matchSource = filterSource === 'all' || l.source === filterSource;
      return matchSearch && matchStatus && matchSource;
    });
  }, [leads, search, filterStatus, filterSource]);

  const departmentsById = useMemo(() => {
    const m = {};
    departments.forEach(d => { m[d._id] = d.name; });
    return m;
  }, [departments]);

  const grouped = useMemo(() => {
    const g = {};
    STATUSES.forEach(s => { g[s] = []; });
    filtered.forEach(l => { const k = l.status || 'new'; if (g[k]) g[k].push(l); });
    return g;
  }, [filtered]);

  const handleDragEnd = useCallback(({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const targetStatus = STATUSES.find(s => s === over.id) || over.id;
    if (targetStatus && STATUSES.includes(targetStatus)) {
      const lead = leads.find(l => l._id === active.id);
      if (lead && lead.status !== targetStatus) {
        dispatch(updateLead({ id: active.id, data: { status: targetStatus } })).catch(() => {
          alertService.error('Error', 'Failed to update lead status');
        });
      }
    }
  }, [leads, dispatch]);

  const openCreate = () => {
    setEditLead(null);
    setFormData({ ...EMPTY_FORM, currency: companyCurrency, pipelineId: pipelines[0]?._id || pipelines[0]?.id || '' });
    setModal(true);
  };

  useEffect(() => {
    if (location.state?.openCreate) {
      openCreate();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]);

  const openEdit = (l) => {
    setEditLead(l);
    setFormData({
      ...EMPTY_FORM,
      ...l,
      tags: (l.tags || []).join(', ')
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let payload;
    try {
      payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      // MongoDB can't cast an empty string to ObjectId — omit rather than send "".
      if (!payload.pipelineId) delete payload.pipelineId;
      if (!payload.departmentId) delete payload.departmentId;
      if (editLead?._id) {
        await dispatch(updateLead({ id: editLead._id, data: payload })).unwrap();
        alertService.success('Updated', 'Lead record updated');
      } else {
        await dispatch(createLead(payload)).unwrap();
        alertService.success('Success', 'New lead created');
      }
      setModal(false);
    } catch (err) {
      if (err && typeof err === 'object' && err.duplicate) {
        const existing = err.existingLead || {};
        const label = existing.name || existing.phoneNumber || existing.email || 'this lead';
        // Cancel (or Esc) keeps the form open; only the explicit button
        // creates a second lead.
        const createAnyway = await alertService.confirm(
          'Duplicate lead found',
          `A lead matching "${label}" already exists (status: ${existing.status || 'unknown'}). Create a new lead anyway?`,
          'warning',
          'Create Anyway'
        );
        if (createAnyway) {
          try {
            await dispatch(createLead({ ...payload, force: true })).unwrap();
            alertService.success('Lead created');
            setModal(false);
          } catch (forceErr) {
            alertService.error("Couldn't create the lead", String(forceErr || 'Please try again'));
          }
        } else if (existing._id) {
          alertService.toast('info', `Existing lead: ${label}`);
        }
      } else {
        alertService.error('Error', err || 'Update failure');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lead) => {
    const confirmed = await alertService.confirm(
      'Delete Lead?',
      `Are you sure you want to permanently delete "${lead.name}"?`,
      'danger',
      'Delete Lead'
    );
    if (confirmed) {
      try {
        await dispatch(deleteLead(lead._id)).unwrap();
        alertService.success('Deleted', 'Lead removed successfully');
      } catch (err) {
        alertService.error('Error', err || 'Deletion failed');
      }
    }
  };

  const handleBulkStatusChange = async (value) => {
    try {
      await api.post('/crm/leads/bulk', { leadIds: bulk.selectedIds, action: 'change_status', value });
      alertService.success('Updated', `${bulk.selectedIds.length} lead(s) updated`);
      bulk.clear();
      refetchLeads();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk status change failed');
    }
  };

  const handleBulkAssign = async (value) => {
    try {
      await api.post('/crm/leads/bulk', { leadIds: bulk.selectedIds, action: 'assign', value });
      alertService.success('Updated', `${bulk.selectedIds.length} lead(s) assigned`);
      bulk.clear();
      refetchLeads();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk assign failed');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await api.post('/crm/leads/bulk', { leadIds: bulk.selectedIds, action: 'delete' });
      alertService.success('Deleted', `${bulk.selectedIds.length} lead(s) deleted`);
      bulk.clear();
      refetchLeads();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Bulk delete failed');
    }
  };

  const handleBulkExport = () => {
    const rows = filtered.filter(l => bulk.selected.has(l._id));
    downloadCsv(
      `leads-selected-${new Date().toISOString().split('T')[0]}.csv`,
      ['Name', 'Phone', 'Email', 'Status', 'Source', 'Score'],
      rows.map(l => [l.name, l.phoneNumber || '', l.email || '', l.status || '', l.source || '', l.score || 0])
    );
  };

  const handleExportLeads = async () => {
    setShowMenu(false);
    try {
      const response = await api.get('/crm/leads/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alertService.error('Export Failed', err.response?.data?.message || 'Could not export leads');
    }
  };

  const handleConvert = (lead) => setConvertingLead(lead);

  const handleConfirmConvert = async (pipelineId, accountName) => {
    try {
      await dispatch(convertLead({ id: convertingLead._id, data: { pipelineId, accountName: accountName || undefined } })).unwrap();
      alertService.success('Converted', 'Lead promoted to deal pipeline');
      setConvertingLead(null);
    } catch (err) {
      alertService.error('Error', err || 'Conversion failed');
    }
  };

  if (loading && leads.length === 0) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className={PAGE}>
      
      {/* Header Area */}
      <PageHeader
        title="Leads"
        subtitle={<>{filtered.length} Leads matching filters</>}
        icon={Users}
        actions={<>
<div className="bg-white border border-[#D8DDE6] rounded p-0.5 flex items-center shadow-sm">
            <button 
              onClick={() => setView('list')}
              className={cn("p-1.5 rounded transition-all", view === 'list' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <List size={14} />
            </button>
            <button 
              onClick={() => setView('kanban')}
              className={cn("p-1.5 rounded transition-all", view === 'kanban' ? 'bg-[#F3F2F2] text-[#0176D3]' : 'text-[#706E6B] hover:text-[#0176D3]')}
            >
              <Columns size={14} />
            </button>
          </div>

          <button onClick={openCreate} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 whitespace-nowrap shrink-0">
            <Plus size={14} /> New Lead
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowMenu(v => !v)}
              title="More options"
              aria-label="More options"
              className="h-9 w-9 flex items-center justify-center bg-white border border-[#D8DDE6] rounded text-[#706E6B] hover:bg-[#F3F2F2] transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute top-full right-0 mt-2 z-20 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] py-1 w-48">
                  <button onClick={handleExportLeads} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] transition-colors flex items-center gap-2">
                    <Upload size={14} /> Export CSV
                  </button>
                  <button onClick={() => { setShowMenu(false); setShowImportModal(true); }} className="w-full text-left px-4 py-2 text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] transition-colors flex items-center gap-2">
                    <Download size={14} /> Import
                  </button>
                </div>
              </>
            )}
          </div>
  </>}
      />

      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Leads"
        importEndpoint="/crm/leads/import"
        sampleHeaders={['Name', 'PhoneNumber', 'Email', 'Source', 'Status', 'Notes']}
        sampleRow={['Jane Doe', '9998887777', 'jane@example.com', 'whatsapp', 'new', 'Interested in premium plan']}
        onImported={() => refetchLeads()}
      />

      <ConvertToDealModal
        open={!!convertingLead}
        lead={convertingLead}
        onClose={() => setConvertingLead(null)}
        onConfirm={handleConfirmConvert}
      />

      {/* Control Bar */}
      <div className="bg-white border border-[#D8DDE6] rounded-lg p-3 flex flex-wrap items-center gap-2 shadow-sm">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
          <input 
            type="text" 
            placeholder="Search leads…" 
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-[#F3F2F2] px-3 h-9 rounded border border-[#D8DDE6]">
            <Filter size={12} className="text-[#706E6B]" />
            <select 
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="bg-transparent text-[12px] font-bold text-[#080707] outline-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 bg-[#F3F2F2] px-3 h-9 rounded border border-[#D8DDE6]">
             <Tag size={12} className="text-[#706E6B]" />
             <select 
               value={filterSource}
               onChange={e => setFilterSource(e.target.value)}
               className="bg-transparent text-[12px] font-bold text-[#080707] outline-none cursor-pointer"
             >
               <option value="all">All Sources</option>
               {SOURCES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
             </select>
          </div>

          {departments.length > 0 && (
            <div className="flex items-center gap-2 bg-[#F3F2F2] px-3 h-9 rounded border border-[#D8DDE6]">
              <Building2 size={12} className="text-[#706E6B]" />
              <select
                value={filterDepartment}
                onChange={e => setFilterDepartment(e.target.value)}
                className="bg-transparent text-[12px] font-bold text-[#080707] outline-none cursor-pointer"
              >
                <option value="all">All Departments</option>
                {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
              </select>
            </div>
          )}

          <FilterBuilder
            fields={LEAD_FILTER_FIELDS}
            conditions={advancedConditions}
            onChange={setAdvancedConditions}
          />

          <button
            onClick={() => setMyLeadsOnly(v => !v)}
            title="Show only leads assigned to me"
            className={cn(
              "h-9 px-3 text-[12px] font-bold rounded border transition-all flex items-center gap-2 whitespace-nowrap shrink-0",
              myLeadsOnly
                ? "bg-[#0176D3] text-white border-[#0176D3]"
                : "bg-[#F3F2F2] text-[#080707] border-[#D8DDE6] hover:border-[#0176D3]"
            )}
          >
            <User size={12} />
            My Leads
          </button>

          <button
            onClick={() => { setSearch(''); setFilterStatus('all'); setFilterSource('all'); setFilterDepartment('all'); setMyLeadsOnly(false); setAdvancedConditions([]); }}
            className="h-9 px-3 text-[13px] font-semibold text-[#0176D3] hover:bg-[#0176D3]/5 rounded transition-all flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            Clear Filters
          </button>

          <SavedViewsBar
            resourceType="lead"
            currentFilters={{ search, status: filterStatus, source: filterSource, advancedConditions }}
            onApply={handleApplyView}
          />
        </div>
      </div>

      {/* Main View Area */}
      {view === 'list' ? (
        <div className="space-y-3">
          <BulkActionBar
            selectedCount={bulk.selectedCount}
            onClear={bulk.clear}
            entityLabel="lead"
            statusLabel="Status"
            statusOptions={STATUSES.map(s => ({ value: s, label: s.toUpperCase() }))}
            onStatusChange={handleBulkStatusChange}
            assignLabel="Assign"
            assignPlaceholder="Assign to…"
            onAssign={handleBulkAssign}
            onDelete={handleBulkDelete}
            onExport={handleBulkExport}
          />
          <div className="sf-table-container">
          <table className="sf-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all leads"
                    checked={bulk.isAllSelected(filtered.map(l => l._id))}
                    onChange={() => bulk.toggleAll(filtered.map(l => l._id))}
                  />
                </th>
                <th>Lead Name</th>
                <th>Status</th>
                <th>Source</th>
                <th>Lead Score</th>
                <th>Email</th>
                <th>Assigned To</th>
                <th>Department</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map(lead => (
                <tr key={lead._id} className="group">
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${lead.name}`}
                      checked={bulk.selected.has(lead._id)}
                      onChange={() => bulk.toggle(lead._id)}
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-[#F3F2F2] flex items-center justify-center font-bold text-[#0176D3] text-[11px] border border-[#D8DDE6]">
                        {lead.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="space-y-0.5">
                        <p onClick={() => navigate(`/dashboard/crm/leads/${lead._id}`)} className="font-bold text-[#0176D3] hover:underline cursor-pointer">{lead.name}</p>
                        <p className="text-[11px] text-[#706E6B] tabular-nums">{lead.phoneNumber || 'No phone'}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <InlineEditSelect
                      value={lead.status || 'new'}
                      options={STATUSES.map(s => ({ value: s, label: s.toUpperCase() }))}
                      badge={<LeadBadge status={lead.status || 'new'} />}
                      onSave={async (newStatus) => {
                        await dispatch(updateLead({ id: lead._id, data: { status: newStatus } }));
                        refetchLeads();
                      }}
                    />
                  </td>
                  <td><span className="sf-badge sf-badge-neutral lowercase">{lead.source || 'manual'}</span></td>
                  <td><ScoreBar score={lead.score || 0} /></td>
                  <td className="text-[#706E6B] font-medium">{lead.email || '—'}</td>
                  <td>
                    {lead.assignedTo ? (
                      <span
                        className={cn(
                          "sf-badge lowercase",
                          currentUserEmail && lead.assignedTo === currentUserEmail ? "sf-badge-manager" : "sf-badge-neutral"
                        )}
                        title={lead.assignedTo}
                      >
                        {lead.assignedTo === currentUserEmail ? 'You' : lead.assignedTo}
                      </span>
                    ) : (
                      <span className="text-[#706E6B]">Unassigned</span>
                    )}
                  </td>
                  <td>
                    {lead.departmentId && departmentsById[lead.departmentId] ? (
                      <span className="sf-badge sf-badge-neutral">{departmentsById[lead.departmentId]}</span>
                    ) : (
                      <span className="text-[#706E6B]">—</span>
                    )}
                  </td>
                  <td className="text-right">
                    <RowActionsMenu actions={[
                      { label: 'Edit', icon: Edit2, onClick: () => openEdit(lead) },
                      { label: 'Convert to Deal', icon: ArrowUpRight, onClick: () => handleConvert(lead) },
                      { label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(lead) },
                    ]} />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={9} className="py-24 text-center">
                    <Users className="mx-auto mb-4 opacity-10" size={48} />
                    <p className="text-sm font-bold text-[#706E6B] uppercase tracking-[0.2em]">Zero records found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {totalLeads > 0 && (
            <div className="flex items-center justify-between bg-white border border-[#E5E5E5] rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <p className="text-[12px] font-medium text-[#706E6B]">
                Showing {((page - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(page * PAGE_SIZE, totalLeads)} of {totalLeads}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-[12px] font-bold text-[#080707] px-2">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="h-8 px-3 flex items-center gap-1 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={({ active }) => setActiveId(active.id)} onDragEnd={handleDragEnd}>
          {/* Fixed board height so the horizontal scrollbar sits at a stable position at
              the bottom of the board instead of drifting away as columns grow taller —
              each column scrolls its own cards internally via its own overflow-y-auto. */}
          <div className="flex gap-6 overflow-x-auto pb-3 h-[65vh] py-2">
            {STATUSES.map(status => (
              <KanbanColumn key={status} status={status} leads={grouped[status] || []} onEdit={openEdit} onConvert={handleConvert} onDelete={handleDelete} />
            ))}
          </div>
          <DragOverlay>
            {activeId ? (
              <div className="w-72 p-4 rounded bg-white border-2 border-[#0176D3] shadow-[0_2px_8px_rgba(0,0,0,0.16)] rotate-1 scale-105 opacity-90 backdrop-blur-md">
                <h4 className="text-[13px] font-bold text-[#080707]">{leads.find(l => l._id === activeId)?.name}</h4>
                <div className="flex items-center justify-between mt-4">
                  <ScoreBar score={leads.find(l => l._id === activeId)?.score || 0} />
                  <LeadBadge status={leads.find(l => l._id === activeId)?.status || 'new'} />
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Form Modal */}
      {modal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModal(false)} />
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editLead ? 'Edit Lead' : 'New Lead'}</h3>
              <button onClick={() => setModal(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Phone</label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input value={formData.phoneNumber} onChange={e => setFormData(p => ({ ...p, phoneNumber: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                    <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Discovery Source</label>
                  <select value={formData.source} onChange={e => setFormData(p => ({ ...p, source: e.target.value }))} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    {SOURCES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Expected Value</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] text-[13px] font-bold">{getCurrencySymbol(formData.currency)}</span>
                    <input type="number" min="0" step="0.01" value={formData.expectedValue} onChange={e => setFormData(p => ({ ...p, expectedValue: e.target.value }))} placeholder="0.00" className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Currency</label>
                  <select value={formData.currency} onChange={e => handleCurrencyChange(e.target.value)} className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]">
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {(pipelines.length > 1 || departments.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pipelines.length > 1 && (
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-[#706E6B]">Pipeline</label>
                      <select
                        value={formData.pipelineId || ''}
                        onChange={e => setFormData(p => ({ ...p, pipelineId: e.target.value }))}
                        className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                      >
                        {pipelines.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  )}
                  {departments.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-[#706E6B]">Department</label>
                      <select
                        value={formData.departmentId || ''}
                        onChange={e => setFormData(p => ({ ...p, departmentId: e.target.value }))}
                        className="w-full h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                      >
                        <option value="">Unassigned</option>
                        {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#706E6B]">Segmentation Tags</label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
                  <input value={formData.tags} onChange={e => setFormData(p => ({ ...p, tags: e.target.value }))} placeholder="Comma separated…" className="w-full h-9 pl-9 pr-4 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]" />
                </div>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
                <button type="button" onClick={() => setModal(false)} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
